import React, { useState } from 'react'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Badge,
  Box,
  Heading,
  Text,
  Skeleton,
  Button,
  IconButton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FaTrash } from 'react-icons/fa'

import { WhitelistResponse } from '../types'
import { formatDate } from '../utils/helpers'
import { whitelistService } from '../services/api'

interface DataTableProps {
  data: WhitelistResponse['data'] | undefined
  isLoading: boolean
  error: unknown
  refetch: () => void
}

const DataTable: React.FC<DataTableProps> = ({
  data,
  isLoading,
  error,
  refetch,
}) => {
  const entries = data?.entries || []
  const toast = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [addressToRemove, setAddressToRemove] = useState<string>('')
  const cancelRef = React.useRef<HTMLButtonElement>(null)

  const handleRemove = async (wallet_address: string) => {
    setAddressToRemove(wallet_address)
    setIsOpen(true)
  }

  const onClose = () => {
    setIsOpen(false)
    setAddressToRemove('')
  }

  const confirmRemove = async () => {
    try {
      await whitelistService.removeFromWhitelist(addressToRemove)
      toast({
        title: 'Success',
        description: 'Address removed from whitelist',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
      refetch()
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to remove address from whitelist',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      onClose()
    }
  }

  if (error) {
    return (
      <Box textAlign="center" py={10} px={6}>
        <Heading as="h2" size="lg" color="red.500">
          Error Loading Data
        </Heading>
        <Text mt={4} color="gray.500">
          {error instanceof Error
            ? error.message
            : 'An unexpected error occurred'}
        </Text>
        <Button
          mt={6}
          colorScheme="blue"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    )
  }

  return (
    <Box>
      <TableContainer borderRadius="md" boxShadow="sm" bg="white">
        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Wallet Address</Th>
              <Th>Description</Th>
              <Th>Added By</Th>
              <Th>Role</Th>
              <Th>Created At</Th>
              <Th>Updated At</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Tr key={index}>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                  <Td><Skeleton height="20px" /></Td>
                </Tr>
              ))
            ) : entries.length > 0 ? (
              entries.map((entry) => (
                <Tr
                  key={entry.wallet_address}    
                >
                  <Td>{entry.id}</Td>
                  <Td>
                    { entry.wallet_address }
                  </Td>
                  <Td>{entry.description}</Td>
                  <Td>{entry.added_by}</Td>
                  <Td>
                    <Badge colorScheme={entry.is_admin ? 'purple' : 'blue'}>
                      {entry.is_admin ? 'Admin' : 'User'}
                    </Badge>
                  </Td>
                  <Td>{formatDate(entry.created_at)}</Td>
                  <Td>{formatDate(entry.updated_at)}</Td>
                  <Td>
                    <IconButton
                      aria-label="Remove from whitelist"
                      icon={<FaTrash />}
                      size="sm"
                      colorScheme="red"
                      variant="ghost"
                      onClick={() => handleRemove(entry.wallet_address)}
                    />
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td colSpan={8} textAlign="center" py={6}>
                  <Text color="gray.500">
                    No Data Whitelist records found
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize='lg' fontWeight='bold'>
              Remove from Whitelist
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove this address from the whitelist? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme='red' onClick={confirmRemove} ml={3}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  )
}

export default DataTable
